import {
  ArrayInput,
  Create,
  Datagrid,
  DateField,
  DeleteButton,
  Edit,
  EditButton,
  email,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ImageField,
  ImageInput,
  Labeled,
  maxValue,
  minValue,
  NumberInput,
  ReferenceField,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  Tab,
  TabbedForm,
  TextField,
  TextInput,
  usePermissions,
} from 'react-admin';
import { ChainTaxPermissionLevelInput, List, Tooltip } from '../components';
import { SearchFilter } from "../components/SearchFilter";
import { PERMISSIONS } from '../constants';
import { PhoneInput } from '../utils/Inputs';
import { formatCellphone } from '../utils/utils';
import { Grid, Typography } from '@mui/material';
import { BillingFrequency, BillingWeekDay } from '../models';

const required = (message = 'Obrigatório') =>
  value => value ? undefined : message;

const ChainList = (props) => (
  <List {...props} title="Rede de postos"
    filters={<SearchFilter />}
  >
    <Datagrid bulkActionButtons={false}>
      <TextField source="name" label="Rede" />
      <FunctionField render={record => formatCellphone(record.phone)} label="Telefone" sortable={false} />
      <TextField source="contactName" label="Nome do contato" />
      <DateField source="createdAt" label="Criado em" locales="pt-BR" />
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

const transform = (data) => {

  if (!data.billingFrequency) {
    data.billingFrequency = '__null__';
  }

  if (!data.billingFrequency || data.billingFrequency === BillingFrequency.Weekly) {
    data.billingIntervalDays = '__null__';
  }
  if (!data.billingFrequency || data.billingFrequency !== BillingFrequency.Weekly) {
    data.billingWeekDay = '__null__';
  }

  if (!data.billingDueDays) {
    data.billingDueDays = '__null__';
  }

  return {
    ...data,
    contacts: data?.contacts.map(({ stationId, companyId, ...rest }) => ({ ...rest })),
    chainCompany: (data?.chainCompany || []).map(item => ({ chainId: item.chainId, companyId: item.companyId, taxRate: item.taxRate })),
  };
}

const ChainEdit = (props) => {

  return (
    <Edit title='Editar rede' {...props} transform={transform}>
      <TabbedForm>
        <FormTab label="Geral">
          <TextInput disabled source="id" label="ID" />
          <TextInput source="name" label="Nome da rede" validate={required()} />
          <TextInput source="contactName" label="Nome do Contato" />
          <PhoneInput source="phone" label="Telefone" />
          <ImageInput source="logofile" label="Logo" accept="image/*">
            <ImageField source="src" label="Imagem" />
          </ImageInput>
        </FormTab>
        <FormTab label="Contatos">
          <ArrayInput source="contacts" label="">
            <SimpleFormIterator fullWidth disableReordering>
              <FormDataConsumer>
                {({ getSource }) => (
                  <>
                    <Grid container lg={8} md={12} spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextInput source={getSource("name")} label="Nome" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextInput source={getSource("position")} label="Função" fullWidth />
                      </Grid>
                    </Grid>
                    <Grid container lg={8} md={12} spacing={2}>
                      <Grid item sx={12} md={4}>
                        <PhoneInput source={getSource("phone")} label="Telefone" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextInput source={getSource("email")} type="email" label="Email" validate={email()} fullWidth />
                      </Grid>
                    </Grid>
                  </>
                )}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Financeiro">
          <Grid container>
            <Grid item xs={12} md={6}>
              <Labeled label="Faturamento - Regras de pagamento" fullWidth>
                <>
                  <Tooltip sxContainer={{ alignItems: 'center', width: '100%' }} placement="top-start" title="Escolha com que frequência os abastecimentos serão faturados">
                    <SelectInput
                      source="billingFrequency"
                      label="Frequência"
                      choices={BillingFrequency.billingFrequencyChoices}
                      fullWidth
                    />
                  </Tooltip>
                  <FormDataConsumer>
                    {({ formData, getSource }) => {
                      let maxDayValue = 30;
                      if (formData.billingFrequency === BillingFrequency.Every10Days) {
                        maxDayValue = 9;
                      } else if (formData.billingFrequency === BillingFrequency.Every15Days) {
                        maxDayValue = 14;
                      } else if (formData.billingFrequency === BillingFrequency.Monthly) {
                        maxDayValue = 30;
                      }

                      return (
                        <>
                          {
                            formData?.billingFrequency === BillingFrequency.Weekly ?
                              <Tooltip sxContainer={{ alignItems: 'center', width: '100%' }} placement="top-start" title="Escolha o dia da semana para gerar o boleto. Serão considerados os abastecimentos da semana anterior (domingo a sábado).">
                                <SelectInput
                                  source={getSource('billingWeekDay')}
                                  label="Dia da semana"
                                  choices={BillingWeekDay.billingWeekDayChoices}
                                  fullWidth
                                />
                              </Tooltip>
                              :
                              formData?.billingFrequency ?
                                <Tooltip sxContainer={{ alignItems: 'center', width: '100%' }} placement="top-start" title="Informe o número de dias após o fechamento do período para gerar o boleto. Exemplo: se for 5, o boleto do dia 1 a 10 será gerado no dia 15.">
                                  <NumberInput source={getSource('billingIntervalDays')} label="Quantidade de dias" validate={[minValue(1), maxValue(maxDayValue), ...(formData.billingFrequency ? [required()] : [])]} fullWidth />
                                </Tooltip>
                                :
                                <></>
                          }
                          <Tooltip sxContainer={{ alignItems: 'center', width: '100%' }} placement="top-start" title="Prazo, em dias, após a geração do boleto para vencimento. Exemplo: se for 5, um boleto gerado no dia 15 vence no dia 20.">
                            <NumberInput source="billingDueDays" label="Prazo de vencimento (dias)" validate={[minValue(1), maxValue(30), ...(formData.billingFrequency ? [required()] : [])]} fullWidth />
                          </Tooltip>
                        </>
                      )
                    }}
                  </FormDataConsumer>
                </>
              </Labeled>

              <Labeled label="Configuração de taxas" fullWidth>
                <Tooltip sxContainer={{ alignItems: 'center', width: '100%' }} placement="top-start" title="Define a taxa padrão aplicada ao abastecimento.">
                  <ChainTaxPermissionLevelInput
                    label="Taxa padrão"
                    source="defaultTax"
                    fullWidth
                  />
                </Tooltip>
              </Labeled>

              <FormDataConsumer>
                {({ formData }) => (
                  <>
                    {
                      !(formData.chainCompany?.length) ?
                        <Typography variant="body1">Vincule transportadoras para habilitar a definição de taxas especiais.</Typography>
                        :
                        <ArrayInput source="chainCompany" label="Taxas especiais" fullWidth>
                          <SimpleFormIterator disableReordering disableAdd disableRemove fullWidth>
                            <FormDataConsumer>
                              {({ formData, getSource, scopedFormData }) => (
                                <SimpleShowLayout record={scopedFormData} sx={{ padding: 0, marginTop: 3, marginBottom: 2 }}>
                                  <ReferenceField
                                    source={'companyId'}
                                    label="Grupo de Transportadora"
                                    reference="companies"
                                  >
                                    <TextField source="name" />
                                  </ReferenceField>
                                </SimpleShowLayout>
                              )}
                            </FormDataConsumer>
                            <ChainTaxPermissionLevelInput
                              label="Taxa"
                              source="taxRate"
                              type="special"
                              fullWidth
                            />
                          </SimpleFormIterator>
                        </ArrayInput>
                    }
                  </>
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}

const ChainCreate = (props) => (
  <Create title='Nova Rede' {...props}>
    <TabbedForm>
      <FormTab label="Geral">
        <TextInput source="name" label="Nome da rede" validate={required()} />
        <TextInput source="contactName" label="Nome do Contato" validate={required()} />
        <PhoneInput source="phone" label="Telefone" />
        <ImageInput source="logofile" label="Logo" accept="image/*">
          <ImageField source="src" label="Imagem" />
        </ImageInput>
      </FormTab>
      <FormTab label="Contatos">
        <ArrayInput source="contacts" label="">
          <SimpleFormIterator fullWidth disableReordering>
            <FormDataConsumer>
              {({ getSource }) => (
                <>
                  <Grid container lg={8} md={12} spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextInput source={getSource("name")} label="Nome" fullWidth />
                    </Grid>
                    <Grid item sx={12} md={4}>
                      <TextInput source={getSource("position")} label="Função" fullWidth />
                    </Grid>
                  </Grid>
                  <Grid container lg={8} md={12} spacing={2}>
                    <Grid item sx={12} md={4}>
                      <PhoneInput source={getSource("phone")} label="Telefone" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextInput source={getSource("email")} type="email" label="Email" validate={email()} fullWidth />
                    </Grid>
                  </Grid>
                </>
              )}
            </FormDataConsumer>
          </SimpleFormIterator >
        </ArrayInput>
      </FormTab>
      <FormTab label="Financeiro">
        <Tooltip sxContainer={{ alignItems: 'center' }} placement="top-start" title="Define a taxa padrão aplicada ao abastecimento, caso não haja uma taxa especial pra transportadora.">
          <ChainTaxPermissionLevelInput
            label="Taxa padrão"
            source="defaultTax"
          />
        </Tooltip>
      </FormTab>
    </TabbedForm>
  </Create>
);

const resource = {
  list: ChainList,
  edit: ChainEdit,
  create: ChainCreate,
  name: 'chains',
  permissions: [PERMISSIONS.ADMIN]
}
export default resource;